import React, { useCallback, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { defineMessages, useIntl } from "react-intl";

import SavedAddressType, {
  ReservedSavedAddressType,
} from "@mapmycustomers/shared/enum/SavedAddressType";
import Address from "@mapmycustomers/shared/types/Address";
import SavedAddress from "@mapmycustomers/shared/types/SavedAddress";
import { AddressPickDetails, AddressPreset, AddressPresets } from "@mapmycustomers/ui";

import { doesOrganizationSupportSavedLocations } from "@app/store/iam";
import { areSavedAddressesLoaded, getSavedAddresses } from "@app/store/savedLocations";
import { createSavedAddress, markSavedAddressUsed } from "@app/store/savedLocations/actions";
import {
  SavedLocationCreateSource,
  SavedLocationEvent,
  SavedLocationSelectSurface,
  trackSavedLocationEvent,
} from "@app/util/analytic/savedLocationsAnalytics";
import locatedToAddress from "@app/util/locatedToAddress";
import addressToSavedAddressFields from "@app/util/savedLocations/addressToSavedAddressFields";
import {
  getSavedAddressList,
  isSavedAddressNameQuery,
  mapSavedAddressList,
} from "@app/util/savedLocations/savedAddressList";

import SavedAddressRow from "./SavedAddressRow";
import SetSavedAddressRow from "./SetSavedAddressRow";
import useSavedAddressesRequest from "./useSavedAddressesRequest";

const messages = defineMessages({
  group: {
    id: "savedLocations.picker.group",
    defaultMessage: "Saved Locations",
    description: "Heading of the saved locations group in an address picker",
  },
});

const setModeTitles = defineMessages<ReservedSavedAddressType>({
  [SavedAddressType.HOME]: {
    id: "savedLocations.picker.setMode.home.title",
    defaultMessage: "Set your Home",
    description: "Heading of an address picker while the rep is choosing their Home",
  },
  [SavedAddressType.WORK]: {
    id: "savedLocations.picker.setMode.work.title",
    defaultMessage: "Set your Work",
    description: "Heading of an address picker while the rep is choosing their Work",
  },
});

const setModeHints = defineMessages<ReservedSavedAddressType>({
  [SavedAddressType.HOME]: {
    id: "savedLocations.picker.setMode.home.hint",
    defaultMessage: "Type an address to set your Home",
    description: "Hint in an address picker while the rep is choosing their Home",
  },
  [SavedAddressType.WORK]: {
    id: "savedLocations.picker.setMode.work.hint",
    defaultMessage: "Type an address to set your Work",
    description: "Hint in an address picker while the rep is choosing their Work",
  },
});

const toSavedPreset = (savedAddress: SavedAddress) => ({
  address: locatedToAddress(savedAddress),
  geoPoint: savedAddress.geoPoint,
  key: String(savedAddress.id),
  label: <SavedAddressRow savedAddress={savedAddress} />,
});

const useSavedAddressPresets = ({
  onBlur,
  onChange,
  onSavedAddressPick,
  surface,
}: {
  onBlur?: () => void;
  onChange?: (address?: Address, details?: AddressPickDetails) => void;
  onSavedAddressPick?: (savedAddress: SavedAddress) => void;
  surface?: SavedLocationSelectSurface;
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  useSavedAddressesRequest();
  const supported = useSelector(doesOrganizationSupportSavedLocations);
  const loaded = useSelector(areSavedAddressesLoaded);
  const savedAddresses = useSelector(getSavedAddresses);
  const [settingType, setSettingType] = useState<ReservedSavedAddressType>();
  // Read by handleChange, which a picker may call from a callback made before Set mode ended
  const settingTypeRef = useRef<ReservedSavedAddressType>();
  // A Set save fills the field late; a change made meanwhile must win
  const latestChangeRef = useRef(0);

  const changeSettingType = useCallback((type?: ReservedSavedAddressType) => {
    settingTypeRef.current = type;
    setSettingType(type);
  }, []);

  const presets = useMemo((): AddressPresets | undefined => {
    if (!supported || !loaded || !surface) {
      return undefined;
    }
    return {
      getGroups: (query) => {
        if (settingType) {
          return [
            {
              key: "setMode",
              label: intl.formatMessage(setModeTitles[settingType]),
              presets: [
                {
                  disabled: true,
                  key: "hint",
                  label: intl.formatMessage(setModeHints[settingType]),
                },
              ],
            },
          ];
        }
        const groupPresets = mapSavedAddressList<AddressPreset>(
          getSavedAddressList(intl, savedAddresses, query),
          toSavedPreset,
          (type) => ({ key: type, label: <SetSavedAddressRow type={type} /> })
        );
        return groupPresets.length
          ? [
              {
                key: "savedLocations",
                label: intl.formatMessage(messages.group),
                presets: groupPresets,
              },
            ]
          : [];
      },
      // In Set mode the rep types an address on purpose
      isPrivateQuery: settingType
        ? undefined
        : (query) => isSavedAddressNameQuery(savedAddresses, query),
      onSelect: ({ key }, query) => {
        if (key === SavedAddressType.HOME || key === SavedAddressType.WORK) {
          changeSettingType(key);
          return;
        }
        const savedAddress = savedAddresses.find(({ id }) => String(id) === key);
        if (!savedAddress) {
          return;
        }
        dispatch(markSavedAddressUsed.request(savedAddress.id));
        trackSavedLocationEvent(SavedLocationEvent.SELECTED, {
          kind: savedAddress.type,
          query_was_empty: !query.trim(),
          surface,
        });
        onSavedAddressPick?.(savedAddress);
      },
    };
  }, [
    changeSettingType,
    dispatch,
    intl,
    loaded,
    onSavedAddressPick,
    savedAddresses,
    settingType,
    supported,
    surface,
  ]);

  const handleChange = useCallback(
    (address?: Address, details?: AddressPickDetails) => {
      const changeId = ++latestChangeRef.current;
      const type = settingTypeRef.current;
      if (type) {
        changeSettingType(undefined);
      }
      if (!type || !address) {
        onChange?.(address, details);
        return;
      }
      const fillIfLatest = (nextAddress: Address, nextDetails?: AddressPickDetails) => {
        if (latestChangeRef.current === changeId) {
          onChange?.(nextAddress, nextDetails);
        }
      };
      dispatch(
        createSavedAddress.request({
          callback: (savedAddress) => {
            if (latestChangeRef.current === changeId) {
              onSavedAddressPick?.(savedAddress);
            }
            fillIfLatest(locatedToAddress(savedAddress), { geoPoint: savedAddress.geoPoint });
          },
          failureCallback: () => fillIfLatest(address, details),
          payload: {
            ...addressToSavedAddressFields(address),
            placeId: details?.placeId,
            type,
          },
          source: SavedLocationCreateSource.RESERVED_ROW,
        })
      );
    },
    [changeSettingType, dispatch, onChange, onSavedAddressPick]
  );

  const handleBlur = useCallback(() => {
    changeSettingType(undefined);
    onBlur?.();
  }, [changeSettingType, onBlur]);

  const isSetting = useCallback(() => !!settingTypeRef.current, []);

  return { isSetting, onBlur: handleBlur, onChange: handleChange, presets, settingType };
};

export default useSavedAddressPresets;
